import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Upload, FileText, Trash2, RefreshCw, Download, CheckSquare, Square, X, Layers, Search, ChevronLeft, ChevronRight } from 'lucide-react';

const DocumentManager = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tenantIdFromUrl = searchParams.get('tenant_id') || '';
  const [documents, setDocuments] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(tenantIdFromUrl);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [stats, setStats] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const searchDebounceRef = useRef(null);

  // File upload state
  const [dragOver, setDragOver] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadSourceUrl, setUploadSourceUrl] = useState('');

  // Sort state (client-side, within current page)
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  // Pagination state
  const PAGE_SIZE = 50;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [allPagesSelected, setAllPagesSelected] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  // Polling ref — auto-refresh when docs are pending/processing
  const pollingRef = useRef(null);

  // Chunks drawer state
  const [drawerDoc, setDrawerDoc] = useState(null);
  const [drawerChunks, setDrawerChunks] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [expandedChunks, setExpandedChunks] = useState(new Set());
  const [editingChunkId, setEditingChunkId] = useState(null);
  const [editingChunkText, setEditingChunkText] = useState('');
  const [savingChunkId, setSavingChunkId] = useState(null);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (drawerDoc) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [drawerDoc]);

  useEffect(() => {
    fetchTenants();
  }, []);

  useEffect(() => {
    if (selectedTenant) {
      // Reset to page 1 whenever tenant/filters change
      setCurrentPage(1);
      fetchDocuments(1);
      fetchStats();
    }
  }, [selectedTenant, selectedStatus]);

  // Debounce search query — wait 400ms after typing before fetching
  useEffect(() => {
    if (!selectedTenant) return;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchDocuments(1);
    }, 400);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchQuery]);

  // Auto-poll every 4s while any document is pending/processing
  useEffect(() => {
    const hasActive = documents.some(d =>
      d.processing_status === 'pending' || d.processing_status === 'processing'
    );
    if (hasActive) {
      if (!pollingRef.current) {
        pollingRef.current = setInterval(() => fetchDocumentsSilent(), 4000);
      }
    } else {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }
  }, [documents]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  const fetchTenants = async () => {
    try {
      const response = await axios.get('/api/tenant/');
      console.log('Tenants response:', response.data);
      setTenants(response.data);
      const list = response.data || [];
      if (list.length === 0) return;
      const fromUrl = searchParams.get('tenant_id');
      const fromStorage = localStorage.getItem('admin_selected_tenant_id');
      const ids = list.map((t) => t.id);
      const pick = [fromUrl, fromStorage].find((id) => id && ids.includes(id)) || list[0].id;
      setSelectedTenant(pick);
      setSearchParams({ tenant_id: pick }, { replace: true });
    } catch (error) {
      toast.error('Failed to load tenants');
      console.error('Error fetching tenants:', error);
    }
  };

  const buildDocParams = (page) => {
    const skip = (page - 1) * PAGE_SIZE;
    const params = new URLSearchParams({
      tenant_id: selectedTenant,
      skip: String(skip),
      limit: String(PAGE_SIZE),
    });
    if (selectedStatus) params.append('processing_status', selectedStatus);
    if (searchQuery.trim()) params.append('search', searchQuery.trim());
    return params;
  };

  const fetchDocuments = async (page = currentPage) => {
    if (!selectedTenant) return;
    setLoading(true);
    try {
      const response = await axios.get(`/api/documents/?${buildDocParams(page)}`);
      setDocuments(response.data.items ?? response.data);
      setTotalDocs(response.data.total ?? response.data.length);
    } catch (error) {
      toast.error('Failed to load documents');
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  // Silent refresh — no spinner, used by the polling interval
  const fetchDocumentsSilent = async (page = currentPage) => {
    if (!selectedTenant) return;
    try {
      const response = await axios.get(`/api/documents/?${buildDocParams(page)}`);
      setDocuments(response.data.items ?? response.data);
      setTotalDocs(response.data.total ?? response.data.length);
    } catch {
      // silent — don't toast on polling failures
    }
  };

  const fetchStats = async () => {
    if (!selectedTenant) return;
    
    try {
      const response = await axios.get(`/api/documents/tenant/${selectedTenant}/knowledge-stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    const files = Array.from(event.dataTransfer.files);
    setSelectedFiles(files);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const uploadFiles = async () => {
    if (!selectedTenant || selectedFiles.length === 0) {
      toast.error('Please select a tenant and files to upload');
      return;
    }

    setUploading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const file of selectedFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('tenant_id', selectedTenant);
        formData.append('priority', '1');
        if (uploadSourceUrl.trim()) {
          formData.append('source_url', uploadSourceUrl.trim());
        }

        await axios.post('/api/documents/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        successCount++;
      } catch (error) {
        errorCount++;
        const detail = error.response?.data?.detail || error.message || 'Unknown error';
        console.error(`Error uploading ${file.name}:`, error);
        toast.error(`Failed to upload ${file.name}: ${detail}`);
      }
    }

    setUploading(false);
    setSelectedFiles([]);

    if (successCount > 0) {
      toast.success(`Successfully uploaded ${successCount} file(s)`);
      fetchDocuments();
      fetchStats();
    }

    if (errorCount > 0) {
      toast.error(`Failed to upload ${errorCount} file(s)`);
    }
  };

  const deleteDocument = async (documentId, filename) => {
    if (!window.confirm(`Are you sure you want to delete "${filename}"?`)) {
      return;
    }

    try {
      await axios.delete(`/api/documents/${documentId}`);
      toast.success('Document deleted successfully');
      fetchDocuments();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete document');
      console.error('Error deleting document:', error);
    }
  };

  const reprocessDocument = async (documentId, filename) => {
    try {
      await axios.post(`/api/documents/${documentId}/reprocess`);
      toast.success(`Reprocessing started for "${filename}"`);
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to start reprocessing');
      console.error('Error reprocessing document:', error);
    }
  };

  // --- Bulk selection helpers ---
  const toggleSelectAll = () => {
    if (selectedIds.size === documents.length && documents.length > 0) {
      setSelectedIds(new Set());
      setAllPagesSelected(false);
    } else {
      setSelectedIds(new Set(documents.map(d => d.id)));
      setAllPagesSelected(false);
    }
  };

  const toggleSelectOne = (id) => {
    setAllPagesSelected(false);
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const bulkDeleteDocuments = async () => {
    const count = allPagesSelected ? totalDocs : selectedIds.size;
    if (count === 0) return;
    if (!window.confirm(`Delete ${count} document(s) permanently?`)) return;
    setBulkLoading(true);
    try {
      const ids = allPagesSelected ? null : [...selectedIds];
      const res = await axios.post('/api/documents/bulk-delete', ids || []);
      toast.success(res.data.message || `Deleted ${count} documents`);
      setSelectedIds(new Set());
      setAllPagesSelected(false);
      fetchDocuments();
      fetchStats();
    } catch (error) {
      toast.error('Bulk delete failed');
      console.error('Bulk delete error:', error);
    } finally {
      setBulkLoading(false);
    }
  };

  const bulkReprocessDocuments = async () => {
    if (selectedIds.size === 0 && !allPagesSelected) return;
    setBulkLoading(true);
    setSelectedIds(new Set());
    setAllPagesSelected(false);
    try {
      const payload = allPagesSelected
        ? { tenant_id: selectedTenant }
        : { document_ids: [...selectedIds] };
      const res = await axios.post('/api/documents/bulk-reprocess', payload);
      toast.success(res.data.message || `Reprocessing queued`);
      fetchDocuments();
    } catch (error) {
      toast.error('Bulk reprocess failed');
      console.error('Bulk reprocess error:', error);
    } finally {
      setBulkLoading(false);
    }
  };

  const reprocessAllFailed = async () => {
    if (!selectedTenant) return;
    if (!window.confirm('Queue all failed documents for reprocessing?')) return;
    setBulkLoading(true);
    try {
      const res = await axios.post('/api/documents/bulk-reprocess', {
        tenant_id: selectedTenant,
        filter_status: 'failed',
      });
      toast.success(res.data.message || 'Reprocessing queued');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to queue documents');
      console.error('Reprocess all failed error:', error);
    } finally {
      setBulkLoading(false);
    }
  };

  // --- Sort helpers ---
  const handleHeaderSort = (col) => {
    if (sortBy === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  };

  const getSortIcon = (col) => {
    if (sortBy !== col) return <span style={{ opacity: 0.3, fontSize: '10px', marginLeft: '4px' }}>⇅</span>;
    return <span style={{ fontSize: '10px', marginLeft: '4px', color: '#667eea' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const STATUS_FILTER_CYCLE = ['', 'pending', 'processing', 'completed', 'failed'];

  const handleStatusHeaderClick = () => {
    const idx = STATUS_FILTER_CYCLE.indexOf(selectedStatus);
    setSelectedStatus(STATUS_FILTER_CYCLE[(idx + 1) % STATUS_FILTER_CYCLE.length]);
  };

  const downloadDocument = async (documentId, filename) => {
    try {
      const response = await axios.get(`/api/documents/${documentId}/download`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Document downloaded successfully');
    } catch (error) {
      toast.error('Failed to download document');
      console.error('Error downloading document:', error);
    }
  };

  const openDrawer = async (doc) => {
    setDrawerDoc(doc);
    setDrawerChunks([]);
    setExpandedChunks(new Set());
    setDrawerLoading(true);
    try {
      const response = await axios.get(`/api/documents/${doc.id}/chunks`);
      setDrawerChunks(response.data.chunks || []);
    } catch (error) {
      toast.error('Failed to load chunks');
      console.error('Error fetching chunks:', error);
    } finally {
      setDrawerLoading(false);
    }
  };

  const closeDrawer = () => {
    setDrawerDoc(null);
    setDrawerChunks([]);
    setExpandedChunks(new Set());
  };

  const toggleChunk = (id) => {
    setExpandedChunks(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const startEditChunk = (chunk) => {
    setEditingChunkId(chunk.id);
    setEditingChunkText(chunk.custom_override ?? chunk.chunk_text ?? '');
  };

  const cancelEditChunk = () => {
    setEditingChunkId(null);
    setEditingChunkText('');
  };

  const saveChunkOverride = async (chunkId) => {
    setSavingChunkId(chunkId);
    try {
      const res = await axios.patch(`/api/documents/chunks/${chunkId}`, {
        custom_override: editingChunkText,
      });
      setDrawerChunks(prev => prev.map(c =>
        c.id === chunkId ? { ...c, custom_override: res.data.custom_override } : c
      ));
      setEditingChunkId(null);
      setEditingChunkText('');
      toast.success('Chunk saved');
    } catch {
      toast.error('Failed to save chunk');
    } finally {
      setSavingChunkId(null);
    }
  };

  const clearChunkOverride = async (chunkId) => {
    setSavingChunkId(chunkId);
    try {
      const res = await axios.patch(`/api/documents/chunks/${chunkId}`, { custom_override: null });
      setDrawerChunks(prev => prev.map(c =>
        c.id === chunkId ? { ...c, custom_override: res.data.custom_override } : c
      ));
      toast.success('Override cleared — original text restored');
    } catch {
      toast.error('Failed to clear override');
    } finally {
      setSavingChunkId(null);
    }
  };

  // Parse chunk_text: extract [heading > path] prefix if present
  const parseChunkText = (text) => {
    const match = text.match(/^\[(.+?)\]\n\n([\s\S]*)/);
    if (match) return { headingPath: match[1], body: match[2] };
    return { headingPath: null, body: text };
  };


  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-warning',
      processing: 'badge-info',
      completed: 'badge-success',
      failed: 'badge-danger'
    };
    return badges[status] || 'badge-secondary';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getSupportedFormats = () => {
    return '.txt, .md, .csv, .json, .pdf, .docx, .xlsx';
  };

  // Client-side sort within the current page
  const sortedDocuments = useMemo(() => {
    if (!sortBy) return documents;
    const arr = [...documents];
    const getCost = (d) => {
      return (d.total_tokens || 0) * 0.02 / 1_000_000 +
             (d.chunk_count || 0) * (500 * 0.15 + 60 * 0.60) / 1_000_000;
    };
    arr.sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'filename') cmp = (a.original_filename || '').localeCompare(b.original_filename || '');
      else if (sortBy === 'file_size') cmp = (a.file_size || 0) - (b.file_size || 0);
      else if (sortBy === 'cost') cmp = getCost(a) - getCost(b);
      else if (sortBy === 'created_at') cmp = new Date(a.created_at) - new Date(b.created_at);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [documents, sortBy, sortDir]);

  return (
    <div className="document-manager">
      {/* Page Header */}
      <div className="page-header">
        <div className="header-content">
          <h1 className="page-title">Document Management</h1>
          <p className="page-subtitle">Upload and manage documents for your knowledge base</p>
        </div>
        <div className="header-actions">
          <select
            className="form-select tenant-selector"
            value={selectedTenant}
            onChange={(e) => {
              const id = e.target.value;
              setSelectedTenant(id);
              setSearchParams(id ? { tenant_id: id } : {}, { replace: true });
              if (id) localStorage.setItem('admin_selected_tenant_id', id);
            }}
          >
            <option value="">Select Tenant</option>
            {tenants.map(tenant => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Knowledge Base Statistics */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#667eea' }}>
              <FileText size={24} color="white" />
            </div>
            <div className="stat-content">
              <h3 className="stat-value">{stats.total_knowledge_items}</h3>
              <p className="stat-label">Total Knowledge Items</p>
              <small className="stat-detail">Documents: {stats.document_count}</small>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#28a745' }}>
              <FileText size={24} color="white" />
            </div>
            <div className="stat-content">
              <h3 className="stat-value">{stats.total_document_words?.toLocaleString() || 0}</h3>
              <p className="stat-label">Document Words</p>
              <small className="stat-detail">Total content volume</small>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#17a2b8' }}>
              <FileText size={24} color="white" />
            </div>
            <div className="stat-content">
              <h3 className="stat-value">{Object.keys(stats.document_categories || {}).length}</h3>
              <p className="stat-label">Categories</p>
              <small className="stat-detail">Organized knowledge areas</small>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: stats.has_knowledge_base ? '#28a745' : '#6c757d' }}>
              <FileText size={24} color="white" />
            </div>
            <div className="stat-content">
              <h3 className="stat-value">{stats.has_knowledge_base ? 'Active' : 'Empty'}</h3>
              <p className="stat-label">Knowledge Base</p>
              <small className="stat-detail">System status</small>
            </div>
          </div>
        </div>
      )}


      {/* Upload Section */}
      <div className="upload-section">
        <div className="section-header">
          <h2 className="section-title">
            <Upload size={20} className="section-icon" />
            Upload Documents
          </h2>
        </div>
        
        <div className="upload-area-container">
          <div
            className={`upload-area ${dragOver ? 'upload-area-active' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <div className="upload-content">
              <Upload size={48} className="upload-icon" />
              <h3 className="upload-title">Drag and drop files here or click to select</h3>
              <p className="upload-subtitle">
                Supported formats: {getSupportedFormats()}
              </p>
              <input
                type="file"
                multiple
                className="file-input"
                id="fileInput"
                onChange={handleFileSelect}
                accept=".txt,.md,.csv,.json,.pdf,.docx,.xlsx"
              />
              <button
                className="btn btn-primary upload-btn"
                onClick={() => document.getElementById('fileInput').click()}
                disabled={!selectedTenant}
              >
                <Upload size={16} />
                Select Files
              </button>
            </div>
          </div>

          {selectedFiles.length > 0 && (
            <div className="selected-files">
              <h4 className="selected-files-title">Selected Files ({selectedFiles.length})</h4>
              <div className="files-list">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="file-item">
                    <div className="file-info">
                      <FileText size={16} className="file-icon" />
                      <span className="file-name">{file.name}</span>
                    </div>
                    <span className="file-size">{formatFileSize(file.size)}</span>
                  </div>
                ))}
              </div>
              <div style={{ margin: '8px 0' }}>
                <input
                  type="url"
                  placeholder="Source URL (optional) — link shown to users in chat citations"
                  value={uploadSourceUrl}
                  onChange={e => setUploadSourceUrl(e.target.value)}
                  style={{ width: '100%', padding: '6px 10px', border: '1px solid #ddd', borderRadius: 4, fontSize: 13 }}
                />
              </div>
              <button
                className="btn btn-success upload-action-btn"
                onClick={uploadFiles}
                disabled={uploading || !selectedTenant}
              >
                {uploading ? (
                  <>
                    <div className="spinner"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={16} />
                    Upload Files
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Documents Section — search, status pills, sortable table */}
      <div className="documents-section">

        {/* Toolbar: title + search + status pills + actions */}
        <div style={{
          padding: '14px 20px 12px', borderBottom: '1px solid #e5e7eb',
          display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center',
          background: '#fff', borderRadius: '8px 8px 0 0',
        }}>
          {/* Title */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '0 0 auto' }}>
            <FileText size={18} color="#667eea" />
            <span style={{ fontWeight: 700, fontSize: '1em', color: '#1f2937' }}>
              Documents
              <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '0.88em', marginLeft: '6px' }}>
                ({totalDocs}{totalDocs !== documents.length ? ` total, ${documents.length} shown` : ''})
              </span>
            </span>
          </div>

          {/* Search */}
          <div style={{ position: 'relative', flex: '1 1 160px', maxWidth: '260px' }}>
            <Search size={14} style={{ position: 'absolute', left: '9px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
            <input
              type="text"
              style={{
                width: '100%', boxSizing: 'border-box',
                paddingLeft: '30px', paddingRight: searchQuery ? '26px' : '8px',
                height: '32px', border: '1px solid #d1d5db', borderRadius: '6px',
                fontSize: '13px', color: '#374151', outline: 'none', background: '#f9fafb',
              }}
              placeholder="Search filename…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0, display: 'flex' }}>
                <X size={13} />
              </button>
            )}
          </div>

          {/* Status filter pills */}
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', flex: '0 0 auto' }}>
            {[
              { value: '', label: 'All' },
              { value: 'completed', label: 'Completed', color: '#15803d', bg: '#dcfce7', border: '#86efac' },
              { value: 'pending',   label: 'Pending',   color: '#92400e', bg: '#fef3c7', border: '#fcd34d' },
              { value: 'processing',label: 'Processing', color: '#1d4ed8', bg: '#dbeafe', border: '#93c5fd' },
              { value: 'failed',    label: 'Failed',    color: '#b91c1c', bg: '#fee2e2', border: '#fca5a5' },
            ].map(({ value, label, color, bg, border }) => {
              const active = selectedStatus === value;
              return (
                <button
                  key={value || 'all'}
                  onClick={() => setSelectedStatus(value)}
                  style={{
                    padding: '3px 10px', borderRadius: '12px', fontSize: '12px', cursor: 'pointer',
                    fontWeight: active ? 600 : 400,
                    background: active ? (bg || '#667eea') : 'white',
                    border: `1px solid ${active ? (border || '#667eea') : '#e5e7eb'}`,
                    color: active ? (color || '#fff') : '#6b7280',
                    transition: 'all 0.15s',
                  }}
                >
                  {label}
                </button>
              );
            })}
            {(selectedStatus || searchQuery) && (
              <button
                onClick={() => { setSelectedStatus(''); setSearchQuery(''); }}
                title="Clear filters"
                style={{ padding: '3px 8px', borderRadius: '12px', fontSize: '12px', cursor: 'pointer', background: 'none', border: '1px solid #e5e7eb', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: '3px' }}
              >
                <X size={11} /> Clear
              </button>
            )}
          </div>

          {/* Right-side actions */}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Retry all failed — only shown when there are failed docs and no selection active */}
            {selectedIds.size === 0 && !allPagesSelected && documents.some(d => d.processing_status === 'failed') && (
              <button
                className="btn btn-sm btn-outline-warning btn-with-icon"
                onClick={reprocessAllFailed}
                disabled={bulkLoading}
                title="Queue all failed documents for reprocessing"
              >
                <RefreshCw size={13} />
                <span>Retry all failed</span>
              </button>
            )}
            {/* Bulk actions */}
            {(selectedIds.size > 0 || allPagesSelected) && (
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                  {allPagesSelected ? `All ${totalDocs}` : selectedIds.size} selected
                </span>
                <button className="btn btn-sm btn-outline-warning btn-with-icon" onClick={bulkReprocessDocuments} disabled={bulkLoading}>
                  <RefreshCw size={13} /><span>Reprocess</span>
                </button>
                <button className="btn btn-sm btn-danger btn-with-icon" onClick={bulkDeleteDocuments} disabled={bulkLoading}>
                  <Trash2 size={13} /><span>Delete</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Processing banner */}
        {documents.some(d => d.processing_status === 'pending' || d.processing_status === 'processing') && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 16px', background: '#fefce8', borderBottom: '1px solid #fde68a', fontSize: '13px', color: '#92400e' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#f59e0b', display: 'inline-block', flexShrink: 0 }} />
            {documents.filter(d => d.processing_status === 'pending' || d.processing_status === 'processing').length} document(s) processing — refreshing automatically
          </div>
        )}

        {/* Cross-page "select all" banner — Gmail-style */}
        {selectedIds.size === documents.length && documents.length > 0 && totalDocs > documents.length && !allPagesSelected && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 16px', background: '#eff6ff', borderBottom: '1px solid #bfdbfe', fontSize: '13px', color: '#1d4ed8' }}>
            All {documents.length} documents on this page are selected.
            <button
              onClick={() => setAllPagesSelected(true)}
              style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontWeight: 600, padding: 0, fontSize: '13px', textDecoration: 'underline' }}
            >
              Select all {totalDocs} documents instead
            </button>
          </div>
        )}

        <div className="documents-container">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading documents...</p>
            </div>
          ) : documents.length === 0 ? (
            <div className="empty-state">
              <FileText size={48} className="empty-icon" />
              <h3>No documents found</h3>
              <p>{selectedStatus || searchQuery ? 'No documents match the current filters.' : 'Upload your first document to get started.'}</p>
            </div>
          ) : (
            <div className="documents-table" style={{ maxHeight: 'calc(100vh - 400px)', overflowY: 'auto', overflowX: 'auto' }}>
              <table className="table" style={{ marginBottom: 0 }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: '#f8f9fa' }}>
                  <tr>
                    <th style={{ width: '36px', padding: '10px 8px' }}>
                      <button
                        className="btn btn-sm btn-link p-0"
                        onClick={toggleSelectAll}
                        title={selectedIds.size === documents.length && documents.length > 0 ? 'Deselect all' : 'Select all on this page'}
                        style={{ color: '#495057' }}
                      >
                        {selectedIds.size === documents.length && documents.length > 0
                          ? <CheckSquare size={16} color="#667eea" />
                          : <Square size={16} />}
                      </button>
                    </th>
                    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleHeaderSort('filename')} title="Sort by name">
                      Document {getSortIcon('filename')}
                    </th>
                    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleHeaderSort('file_size')} title="Sort by size">
                      Size {getSortIcon('file_size')}
                    </th>
                    <th
                      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', color: selectedStatus ? '#667eea' : undefined }}
                      onClick={handleStatusHeaderClick}
                      title={`Click to cycle status filter — current: ${selectedStatus || 'All'}`}
                    >
                      Status {selectedStatus
                        ? <span style={{ fontSize: '10px', marginLeft: '3px', color: '#667eea', fontWeight: 700 }}>({selectedStatus}) ×</span>
                        : <span style={{ opacity: 0.35, fontSize: '10px', marginLeft: '4px' }}>⇅</span>}
                    </th>
                    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleHeaderSort('cost')} title="Sort by estimated indexing cost">
                      Cost {getSortIcon('cost')}
                    </th>
                    <th>Tags</th>
                    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleHeaderSort('created_at')} title="Sort by date">
                      Created {getSortIcon('created_at')}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDocuments.map(doc => (
                    <tr key={doc.id} className="document-row" style={selectedIds.has(doc.id) ? { backgroundColor: '#f0f4ff' } : {}}>
                      <td>
                        <button className="btn btn-sm btn-link p-0" onClick={() => toggleSelectOne(doc.id)} style={{ color: '#495057' }}>
                          {selectedIds.has(doc.id) ? <CheckSquare size={16} color="#667eea" /> : <Square size={16} />}
                        </button>
                      </td>
                      <td className="document-info">
                        <div className="document-name">
                          <FileText size={16} className="document-icon" />
                          <strong>{doc.original_filename}</strong>
                        </div>
                        {doc.subcategory && <div className="document-subcategory">{doc.subcategory}</div>}
                      </td>
                      <td className="file-size-cell" style={{ whiteSpace: 'nowrap' }}>{formatFileSize(doc.file_size)}</td>
                      <td>
                        <span className={`badge ${getStatusBadge(doc.processing_status)}`}>{doc.processing_status}</span>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {doc.chunk_count > 0 ? (() => {
                          const embeddingCost = (doc.total_tokens || 0) * 0.02 / 1_000_000;
                          const contextCost = (doc.chunk_count || 0) * (500 * 0.15 + 60 * 0.60) / 1_000_000;
                          const total = embeddingCost + contextCost;
                          return (
                            <span title={`Embedding: $${embeddingCost.toFixed(5)} · Context prefixes: $${contextCost.toFixed(5)}`} style={{ fontSize: '0.82em', color: '#374151', cursor: 'default' }}>
                              ${total.toFixed(4)}
                            </span>
                          );
                        })() : <span style={{ color: '#9ca3af', fontSize: '0.82em' }}>—</span>}
                      </td>
                      <td className="tags-cell">
                        {doc.tags && doc.tags.length > 0 ? (
                          <div className="tags-container">
                            {doc.tags.slice(0, 2).map(tag => <span key={tag} className="tag">{tag}</span>)}
                            {doc.tags.length > 2 && <span className="tag-count">+{doc.tags.length - 2}</span>}
                          </div>
                        ) : <span className="text-muted">-</span>}
                      </td>
                      <td className="date-cell" style={{ whiteSpace: 'nowrap' }}>
                        {new Date(doc.created_at).toLocaleDateString()}
                      </td>
                      <td className="actions-cell">
                        <div className="action-buttons">
                          <button className="btn btn-sm btn-outline-secondary btn-with-icon" onClick={() => openDrawer(doc)} title="View chunks and metadata">
                            <Layers size={14} /><span>Chunks</span>
                          </button>
                          <button className="btn btn-sm btn-outline-primary btn-with-icon" onClick={() => downloadDocument(doc.id, doc.original_filename)} title="Download document file">
                            <Download size={14} /><span>Download</span>
                          </button>
                          {doc.processing_status === 'failed' && (
                            <button className="btn btn-sm btn-outline-warning btn-with-icon" onClick={() => reprocessDocument(doc.id, doc.original_filename)} title="Retry processing this document">
                              <RefreshCw size={14} /><span>Retry</span>
                            </button>
                          )}
                          <button className="btn btn-sm btn-outline-danger btn-with-icon" onClick={() => deleteDocument(doc.id, doc.original_filename)} title="Delete document permanently">
                            <Trash2 size={14} /><span>Delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalDocs > PAGE_SIZE && (() => {
            const totalPages = Math.ceil(totalDocs / PAGE_SIZE);
            const goTo = (p) => { setCurrentPage(p); fetchDocuments(p); };
            const pages = [];
            if (totalPages <= 7) {
              for (let i = 1; i <= totalPages; i++) pages.push(i);
            } else {
              pages.push(1);
              if (currentPage > 3) pages.push('...');
              for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
              if (currentPage < totalPages - 2) pages.push('...');
              pages.push(totalPages);
            }
            const btnBase = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '34px', height: '34px', padding: '0 8px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s ease', background: 'white', color: '#374151' };
            const btnActive = { ...btnBase, background: '#667eea', borderColor: '#667eea', color: '#fff', boxShadow: '0 2px 6px rgba(102,126,234,0.35)' };
            const btnDisabled = { ...btnBase, opacity: 0.4, cursor: 'not-allowed' };
            return (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderTop: '1px solid #e5e7eb', background: '#f9fafb', flexWrap: 'wrap', gap: '8px' }}>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>
                  Showing {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, totalDocs)} of <strong>{totalDocs}</strong> documents
                </span>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <button style={currentPage === 1 || loading ? btnDisabled : btnBase} disabled={currentPage === 1 || loading} onClick={() => goTo(currentPage - 1)} title="Previous page"><ChevronLeft size={15} /></button>
                  {pages.map((p, idx) =>
                    p === '...'
                      ? <span key={`e-${idx}`} style={{ padding: '0 4px', color: '#9ca3af', fontSize: '13px', lineHeight: '34px' }}>…</span>
                      : <button key={p} style={p === currentPage ? btnActive : btnBase} onClick={() => p !== currentPage && goTo(p)}
                          onMouseEnter={(e) => { if (p !== currentPage) Object.assign(e.currentTarget.style, { borderColor: '#667eea', color: '#667eea' }); }}
                          onMouseLeave={(e) => { if (p !== currentPage) Object.assign(e.currentTarget.style, { borderColor: '#e5e7eb', color: '#374151' }); }}>{p}</button>
                  )}
                  <button style={currentPage >= totalPages || loading ? btnDisabled : btnBase} disabled={currentPage >= totalPages || loading} onClick={() => goTo(currentPage + 1)} title="Next page"><ChevronRight size={15} /></button>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Chunks Drawer */}
      {drawerDoc && (
        <>
          {/* Overlay */}
          <div
            onClick={closeDrawer}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
              zIndex: 1000, backdropFilter: 'blur(2px)'
            }}
          />

          {/* Panel */}
          <div style={{
            position: 'fixed', top: 0, right: 0, bottom: 0,
            width: '700px', maxWidth: '95vw', background: '#fff', zIndex: 1001,
            display: 'flex', flexDirection: 'column',
            boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
          }}>
            {/* Drawer Header */}
            <div style={{
              padding: '20px 24px 16px', borderBottom: '1px solid #e9ecef',
              display: 'flex', flexDirection: 'column', gap: '10px',
              background: '#f8f9fa',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                  <FileText size={20} color="#667eea" style={{ flexShrink: 0 }} />
                  <span style={{
                    fontWeight: 700, fontSize: '0.95em', color: '#1a1a1a',
                    wordBreak: 'break-word', lineHeight: 1.3,
                  }}>
                    {drawerDoc.original_filename}
                  </span>
                </div>
                <button
                  onClick={closeDrawer}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    padding: '4px', color: '#6c757d', flexShrink: 0,
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Summary badges */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                <span style={{ background: '#667eea', color: '#fff', borderRadius: '12px', padding: '2px 10px', fontSize: '0.78em', fontWeight: 600 }}>
                  {drawerChunks.length} chunks
                </span>
                <span style={{ background: '#e9ecef', color: '#495057', borderRadius: '12px', padding: '2px 10px', fontSize: '0.78em', fontWeight: 600 }}>
                  {drawerChunks.reduce((s, c) => s + (c.token_count || 0), 0).toLocaleString()} tokens
                </span>
                {(() => {
                  const totalTok = drawerChunks.reduce((s, c) => s + (c.token_count || 0), 0);
                  const n = drawerChunks.length;
                  if (!n) return null;
                  const cost = totalTok * 0.02 / 1_000_000 + n * (500 * 0.15 + 60 * 0.60) / 1_000_000;
                  return (
                    <span
                      title="Embedding + contextual retrieval cost"
                      style={{ background: '#fef3c7', color: '#92400e', borderRadius: '12px', padding: '2px 10px', fontSize: '0.78em', fontWeight: 600 }}
                    >
                      ~${cost.toFixed(4)} indexing
                    </span>
                  );
                })()}
                {drawerDoc.category && (
                  <span style={{ background: '#e8f5e9', color: '#2e7d32', borderRadius: '12px', padding: '2px 10px', fontSize: '0.78em', fontWeight: 600 }}>
                    {drawerDoc.category}
                  </span>
                )}
                <span style={{ background: drawerDoc.processing_status === 'completed' ? '#e8f5e9' : '#fff3cd', color: drawerDoc.processing_status === 'completed' ? '#2e7d32' : '#856404', borderRadius: '12px', padding: '2px 10px', fontSize: '0.78em', fontWeight: 600 }}>
                  {drawerDoc.processing_status}
                </span>
              </div>
            </div>

            {/* Chunk List */}
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {drawerLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                  <div className="spinner" style={{ margin: '0 auto 12px' }} />
                  <p>Loading chunks...</p>
                </div>
              ) : drawerChunks.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                  <Layers size={40} style={{ marginBottom: '12px', opacity: 0.4 }} />
                  <p>No chunks found for this document.</p>
                </div>
              ) : drawerChunks.map((chunk) => {
                const { headingPath, body } = parseChunkText(chunk.chunk_text || '');
                const meta = chunk.metadata || {};
                const isExpanded = expandedChunks.has(chunk.id);

                return (
                  <div key={chunk.id} style={{
                    border: '1px solid #e2e8f0', borderRadius: '8px',
                    overflow: 'hidden', background: '#fff',
                    boxShadow: isExpanded ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
                    flexShrink: 0,
                  }}>
                    {/* Clickable header */}
                    <div
                      onClick={() => toggleChunk(chunk.id)}
                      style={{
                        cursor: 'pointer', userSelect: 'none',
                        padding: '10px 14px',
                        background: isExpanded ? '#eef0ff' : '#f8f9fa',
                        borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none',
                      }}
                    >
                      {/* Row 1: index + heading path + expand arrow */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
                        <span style={{
                          background: '#667eea', color: '#fff',
                          borderRadius: '4px', padding: '2px 8px',
                          fontSize: '0.72em', fontWeight: 700, flexShrink: 0,
                        }}>
                          #{chunk.chunk_index + 1}
                        </span>
                        <span style={{
                          flex: 1, minWidth: 0,
                          fontSize: '0.82em', color: '#374151', fontWeight: 600,
                          overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                        }}>
                          {headingPath ? headingPath.replace(/ > /g, ' › ') : '—'}
                        </span>
                        <span style={{ color: '#667eea', fontSize: '0.8em', fontWeight: 700, flexShrink: 0 }}>
                          {isExpanded ? '▲' : '▼'}
                        </span>
                      </div>
                      {/* Row 2: badges */}
                      <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.7em', color: '#6b7280', background: '#e5e7eb', borderRadius: '10px', padding: '1px 8px' }}>
                          {chunk.token_count} tokens
                        </span>
                        {chunk.custom_override && <span style={{ fontSize: '0.7em', background: '#fef3c7', color: '#92400e', borderRadius: '10px', padding: '1px 8px', fontWeight: 700 }}>✏ override</span>}
                        {meta.has_table && <span style={{ fontSize: '0.7em', background: '#d1ecf1', color: '#0c5460', borderRadius: '10px', padding: '1px 8px' }}>table</span>}
                        {meta.has_numbered_list && <span style={{ fontSize: '0.7em', background: '#d4edda', color: '#155724', borderRadius: '10px', padding: '1px 8px' }}>numbered</span>}
                        {meta.has_list && !meta.has_numbered_list && <span style={{ fontSize: '0.7em', background: '#d4edda', color: '#155724', borderRadius: '10px', padding: '1px 8px' }}>list</span>}
                        {meta.has_dates && <span style={{ fontSize: '0.7em', background: '#fff3cd', color: '#856404', borderRadius: '10px', padding: '1px 8px' }}>dates</span>}
                        {meta.has_phone && <span style={{ fontSize: '0.7em', background: '#e2d9f3', color: '#6f42c1', borderRadius: '10px', padding: '1px 8px' }}>phone</span>}
                        {meta.has_email && <span style={{ fontSize: '0.7em', background: '#e2d9f3', color: '#6f42c1', borderRadius: '10px', padding: '1px 8px' }}>email</span>}
                        {meta.numeric_density > 2 && <span style={{ fontSize: '0.7em', background: '#fde8d8', color: '#8b3e03', borderRadius: '10px', padding: '1px 8px' }}>numeric</span>}
                      </div>
                    </div>

                    {/* Expanded content: metadata grid + text body + edit */}
                    {isExpanded && (
                      <>
                        {/* Metadata grid */}
                        <div style={{
                          padding: '10px 14px', borderBottom: '1px solid #e2e8f0',
                          background: '#f8faff',
                          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px 12px',
                        }}>
                          {[
                            ['Position', meta.doc_position || '—'],
                            ['Words', meta.word_count ?? '—'],
                            ['Tokens', chunk.token_count ?? '—'],
                            ['Heading depth', meta.heading_depth ?? '—'],
                            ['Section title', meta.section_title || '—'],
                            ['Block types', (meta.block_types || []).join(', ') || '—'],
                            ['Category', meta.category || '—'],
                            ['Source URL', meta.source_url || '—'],
                            ['List items', meta.list_item_count ?? '—'],
                            ['Table shape', meta.table_shape ? `${meta.table_shape[0]}×${meta.table_shape[1]}` : '—'],
                            ['Numeric density', meta.numeric_density != null ? meta.numeric_density.toFixed(1) + '%' : '—'],
                            ['First in section', meta.is_first_in_section ? 'yes' : 'no'],
                            ['Has table', meta.has_table ? 'yes' : 'no'],
                            ['Has dates', meta.has_dates ? 'yes' : 'no'],
                            ['Has URLs', meta.has_urls ? 'yes' : 'no'],
                            ['Has phone', meta.has_phone ? 'yes' : 'no'],
                            ['Has email', meta.has_email ? 'yes' : 'no'],
                          ].map(([label, value]) => (
                            <div key={label}>
                              <div style={{ fontSize: '0.68em', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>{label}</div>
                              <div style={{ fontSize: '0.78em', color: '#374151', fontWeight: 500, wordBreak: 'break-all' }}>{String(value)}</div>
                            </div>
                          ))}
                        </div>

                        {/* Text body / edit area */}
                        {editingChunkId === chunk.id ? (
                          <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{ fontSize: '0.75em', color: '#6366f1', fontWeight: 600, marginBottom: 2 }}>
                              Editing override — this text replaces the original in RAG responses
                            </div>
                            <textarea
                              value={editingChunkText}
                              onChange={e => setEditingChunkText(e.target.value)}
                              rows={10}
                              style={{
                                width: '100%', boxSizing: 'border-box',
                                fontFamily: 'inherit', fontSize: '0.84em', lineHeight: 1.7,
                                border: '1.5px solid #6366f1', borderRadius: 6, padding: '8px 10px',
                                resize: 'vertical', outline: 'none',
                              }}
                            />
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button
                                onClick={() => saveChunkOverride(chunk.id)}
                                disabled={savingChunkId === chunk.id}
                                style={{ padding: '5px 16px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.82em', fontWeight: 600 }}
                              >
                                {savingChunkId === chunk.id ? 'Saving…' : 'Save'}
                              </button>
                              <button
                                onClick={cancelEditChunk}
                                style={{ padding: '5px 14px', background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: '0.82em' }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div style={{ padding: '12px 14px' }}>
                            {chunk.custom_override && (
                              <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontSize: '0.72em', background: '#fef3c7', color: '#92400e', borderRadius: 10, padding: '2px 8px', fontWeight: 700 }}>
                                  ✏ Custom override active
                                </span>
                                <button
                                  onClick={() => clearChunkOverride(chunk.id)}
                                  disabled={savingChunkId === chunk.id}
                                  style={{ fontSize: '0.72em', color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                                >
                                  Clear
                                </button>
                              </div>
                            )}
                            <div style={{
                              fontSize: '0.84em', lineHeight: 1.7, color: '#1f2937',
                              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                              fontFamily: 'inherit',
                            }}>
                              {(chunk.custom_override || body).replace(/\n{3,}/g, '\n\n').trim()}
                            </div>
                            <button
                              onClick={() => startEditChunk(chunk)}
                              style={{
                                marginTop: 10, padding: '4px 14px',
                                background: '#f8faff', color: '#6366f1',
                                border: '1px solid #c7d2fe', borderRadius: 6,
                                cursor: 'pointer', fontSize: '0.78em', fontWeight: 600,
                              }}
                            >
                              ✏ Edit / Override
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DocumentManager;